import React, { useCallback, useEffect, useMemo, useState } from "react";
import AbsoluteContainer from "../containers/AbsoluteContainer";
import RelativeContainer from "../containers/RelativeContainer";

import addPin from "../../assets/icons/add-pin.svg";
import { useSpring, animated } from "react-spring";
import { getUploadedImages, processDroppedFile } from "../../utils/fileUtils";
import { cloneDeep } from "lodash";
import { generateRandomId } from "../../utils/objectUtils";
import useError from "../../hooks/useError";
import MoodboardItem from "./MoodboardItem";
import { useDrag } from "react-use-gesture";

function UploadWidget({ x, y, onUpload, deactivate }) {
  const [prevPos, setPrevPos] = useState([0, 0]);

  useEffect(() => {
    if (x !== undefined && y !== undefined) {
      setPrevPos([x, y]);
    }
  }, [x, y]);

  const { xy } = useSpring({
    xy: prevPos,
  });

  const pinProps = useSpring({
    scale: x !== undefined ? 1 : 0.8,
    opacity: x !== undefined ? 1 : 0,
  });

  const bind = useDrag(
    ({ tap }) => {
      if (tap && x) {
        getUploadedImages(({ images, error }) => {
          if (images && images.length > 0) {
            // Upload first image at click position, rest offset
            images.forEach((img, index) => {
              onUpload({
                result: img.result,
                aspectRatio: img.aspectRatio,
                offsetIndex: index,
              });
            });
          }
        });
      }
    },
    { filterTaps: true }
  );

  return (
    <animated.div
      style={{
        position: "absolute",
        height: 0.1,
        width: 0.1,
        overflow: "visible",
        transform: xy.interpolate((x, y) => `translate3d(${x}px, ${y}px, 0px)`),
      }}
    >
      <animated.img
        {...bind()}
        src={addPin}
        draggable={false}
        alt="add-pin"
        style={{
          cursor: "pointer",
          width: 40,
          position: "absolute",
          bottom: -5,
          right: -20,
          opacity: pinProps.opacity,
        }}
        {...pinProps}
      />
    </animated.div>
  );
}

function SelectorItem({ text, selected, first, last, select }) {
  return (
    <RelativeContainer
      onClick={select}
      style={{
        width: "auto",
        cursor: "pointer",
        paddingLeft: first ? "16px" : "10px",
        paddingRight: last ? "16px" : "10px",
        color: selected ? "whitesmoke" : "#565656",
        background: selected ? "#565656" : "whitesmoke",
        transition: "all 500ms",
      }}
    >
      {text}
    </RelativeContainer>
  );
}

function Selector({ selected, select, options }) {
  const selectedIndex = useMemo(
    () => options.indexOf(selected),
    [selected, options]
  );

  useEffect(() => {
    if (!selected && options) {
      select(options[0]);
    }
  }, [select, options, selected]);
  return (
    <AbsoluteContainer
      style={{
        top: 10,
        left: 10,
        height: 30,
        width: 120,
      }}
    >
      <RelativeContainer
        style={{
          width: "auto",
          flexFlow: "row",
          overflow: "hidden",
          fontSize: "14px",
          background: "whitesmoke",
          borderRadius: 50,
        }}
      >
        {options.map((option, index) => (
          <SelectorItem
            key={`selector_option_${option}`}
            selected={selectedIndex === index}
            select={() => select(option)}
            first={index === 0}
            last={index === options.length - 1}
            text={option}
          />
        ))}
      </RelativeContainer>
    </AbsoluteContainer>
  );
}

function ItemControlPanel({ item, onChange, onDelete }) {
  const props = useSpring({ opacity: item ? 1 : 0 });

  return (
    <AbsoluteContainer
      style={{
        right: 20,
        bottom: 20,
        height: "auto",
        width: "auto",
        pointerEvents: item ? "auto" : "none",
      }}
    >
      <animated.span
        onClick={onDelete}
        style={{
          cursor: "pointer",
          padding: "5px 20px",
          borderRadius: "20px",
          filter: "drop-shadow(0px 0px 4px #666)",
          fontSize: "14px",
          background: "whitesmoke",
          color: "red",
          ...props,
        }}
      >
        DELETE ITEM
      </animated.span>
    </AbsoluteContainer>
  );
}

// Mobile floating add button
function MobileAddButton({ visible, onAdd }) {
  const props = useSpring({
    opacity: visible ? 1 : 0,
    transform: visible ? "scale(1)" : "scale(0.8)",
  });

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      onAdd();
    },
    [onAdd]
  );

  return (
    <animated.div
      onClick={handleClick}
      style={{
        position: "absolute",
        bottom: 80,
        right: 20,
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "#565656",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        userSelect: "none",
        zIndex: 1000,
        pointerEvents: visible ? "auto" : "none",
        ...props,
      }}
    >
      <span
        style={{
          fontSize: 32,
          fontWeight: 300,
          color: "white",
          lineHeight: 1,
        }}
      >
        +
      </span>
    </animated.div>
  );
}

export default function Moodboard({ height, width, moodboard }) {
  const { items, adminFunctions } = moodboard || {};
  const [selectedMode, setSelectedMode] = useState(null);
  const [editedItems, setEditedItems] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const { setError } = useError();

  // Detect mobile/touch devices
  const isMobile = useMemo(() => {
    if (typeof window !== "undefined") {
      return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    }
    return false;
  }, []);

  useEffect(() => {
    setEditedItems(items);
  }, [items]);

  const editActive = useMemo(
    () => adminFunctions && selectedMode === "Edit",
    [selectedMode, adminFunctions]
  );

  const handleUpload = useCallback(
    ({ result, aspectRatio, offsetIndex = 0 }) => {
      if (selectedItemId) {
        let newItems = cloneDeep(editedItems);
        if (!newItems) newItems = {};

        // Calculate the next zIndex (higher than all existing items)
        const maxZIndex = Object.values(newItems).reduce(
          (max, item) => Math.max(max, item.zIndex || 0),
          0
        );

        let newId = generateRandomId();
        // Offset each subsequent image slightly
        const offset = offsetIndex * 30;
        let item = {
          ...selectedItemId,
          x: (selectedItemId.x || 0) + offset,
          y: (selectedItemId.y || 0) + offset,
          height: 150,
          dataUrl: result,
          aspectRatio,
          zIndex: maxZIndex + 1 + offsetIndex,
        };
        adminFunctions.saveItem(newId, item, ({ error }) => {
          if (error) setError(error);
        });
        if (offsetIndex === 0) {
          setSelectedItemId(newId);
        }
      }
    },
    [selectedItemId, editedItems, adminFunctions, setError]
  );

  const deleteSelectedItem = useCallback(() => {
    if (selectedItemId) {
      adminFunctions.deleteItem(selectedItemId, ({ success }) => {
        if (success) {
          setSelectedItemId(null);
        }
      });
    }
  }, [selectedItemId, adminFunctions]);

  //TODO : adjust depending on items
  const bounds = useMemo(() => {
    let bottom = 0;
    let right = 0;
    let top = 0;
    let left = 0;

    let cx = width / 2;
    let cy = height / 2;

    if (items) {
      let itemValues = Object.values(items);
      if (itemValues.length) {
        bottom = Math.max(
          Math.min(...itemValues.map(({ height, y }) => y - height / 2 - 20)) *
            -1 -
            cy,
          0
        );
        right = Math.max(
          Math.min(
            ...itemValues.map(
              ({ height, x, aspectRatio }) =>
                x - (height * aspectRatio) / 2 - 20
            )
          ) *
            -1 -
            cx,
          0
        );
        top = Math.min(
          Math.max(...itemValues.map(({ y, height }) => y + height / 2 + 20)) *
            -1 +
            cy,
          0
        );

        left = Math.min(
          Math.max(
            ...itemValues.map(
              ({ x, height, aspectRatio }) =>
                x + (height * aspectRatio) / 2 + 20
            )
          ) *
            -1 +
            cx,
          0
        );
      }
    }

    return { left, top, right, bottom };
  }, [items, height, width]);

  const [{ panPosition }, setPanPosition] = useSpring(() => ({
    panPosition: [0, 0],
  }));

  useEffect(() => {
    if (!editActive) {
      setSelectedItemId(null);
      setPanPosition({ panPosition: [0, 0] });
    }
  }, [editActive, setPanPosition]);

  const handleClick = useCallback(
    (xy) => {
      if (editActive) {
        if (selectedItemId) {
          setSelectedItemId(null);
        } else {
          let [panX, panY] = panPosition.getValue();
          setSelectedItemId({
            x: xy[0] - width / 2 - panX,
            y: xy[1] - height / 2 - panY,
          });
        }
      }
    },
    [selectedItemId, editActive, width, height, panPosition]
  );

  const handleSelectItem = useCallback(
    (id) => {
      if (editActive && items[id]) {
        setSelectedItemId(id);
      }
    },
    [editActive, items]
  );

  useEffect(() => {
    if (!adminFunctions) {
      setSelectedItemId(null);
    }
  }, [adminFunctions]);

  const handleMoveItem = useCallback(
    (itemId, { mx, my }, callback) => {
      let newItem = items[itemId];
      newItem.x += mx;
      newItem.y += my;
      adminFunctions.saveItem(itemId, newItem, callback);
    },
    [items, adminFunctions]
  );

  const handleScaleItem = useCallback(
    (itemId, scaleAmount, callback) => {
      let newItem = items[itemId];
      newItem.height += scaleAmount;
      adminFunctions.saveItem(itemId, newItem, callback);
    },
    [items, adminFunctions]
  );

  // Mobile add button handler - places image at center
  const handleMobileAdd = useCallback(() => {
    getUploadedImages(({ images, error }) => {
      if (images && images.length > 0) {
        let newItems = cloneDeep(editedItems);
        if (!newItems) newItems = {};

        let baseZIndex = Object.values(newItems).reduce(
          (max, item) => Math.max(max, item.zIndex || 0),
          0
        );

        images.forEach((img, index) => {
          let newId = generateRandomId();
          // Offset each image slightly so they don't stack exactly
          const offset = index * 30;
          let item = {
            x: offset,
            y: offset,
            height: 150,
            dataUrl: img.result,
            aspectRatio: img.aspectRatio,
            zIndex: baseZIndex + 1 + index,
          };
          adminFunctions.saveItem(newId, item, ({ error: saveError }) => {
            if (saveError) setError(saveError);
          });
          if (index === 0) {
            setSelectedItemId(newId);
          }
        });
      } else if (error) {
        setError(error);
      }
    });
  }, [editedItems, adminFunctions, setError]);

  // Drag and drop handlers for desktop
  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (editActive) {
        setIsDragging(true);
      }
    },
    [editActive]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (!editActive || !adminFunctions) return;

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        // Capture position data BEFORE async callback (e.currentTarget becomes null after)
        const dropX = e.clientX - width / 2;
        const dropY = e.clientY - height / 2;
        const [panX, panY] = panPosition.getValue();
        const finalX = dropX - panX;
        const finalY = dropY - panY;

        // Process all dropped files
        Array.from(files).forEach((file, index) => {
          processDroppedFile(file, ({ result, aspectRatio, error }) => {
            if (result) {
              let newItems = cloneDeep(editedItems);
              if (!newItems) newItems = {};

              const maxZIndex = Object.values(newItems).reduce(
                (max, item) => Math.max(max, item.zIndex || 0),
                0
              );

              let newId = generateRandomId();
              // Offset each image slightly
              const offset = index * 30;
              let item = {
                x: finalX + offset,
                y: finalY + offset,
                height: 150,
                dataUrl: result,
                aspectRatio,
                zIndex: maxZIndex + 1 + index,
              };
              adminFunctions.saveItem(newId, item, ({ error: saveError }) => {
                if (saveError) setError(saveError);
              });
              if (index === 0) {
                setSelectedItemId(newId);
              }
            } else if (error) {
              setError(error);
            }
          });
        });
      }
    },
    [
      editActive,
      adminFunctions,
      editedItems,
      width,
      height,
      panPosition,
      setError,
    ]
  );

  const bind = useDrag(
    ({ offset, tap, xy }) => {
      if (tap) {
        handleClick(xy);
      }
      setPanPosition({ panPosition: offset });
    },
    { bounds: editActive ? undefined : bounds, filterTaps: true }
  );

  return (
    <RelativeContainer
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        position: "fixed",
        height,
        overflow: "hidden",
        width,
      }}
    >
      {/* Drag and drop overlay indicator */}
      {isDragging && editActive && (
        <AbsoluteContainer
          style={{
            position: "fixed",
            height,
            width,
            background: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(4px)",
            zIndex: 9998,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              padding: "30px 50px",
              background: "white",
              borderRadius: 20,
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 48, display: "block", marginBottom: 10 }}>
              📷
            </span>
            <span style={{ fontSize: 24, color: "#333" }}>drop image here</span>
          </div>
        </AbsoluteContainer>
      )}
      <animated.div
        {...bind()}
        style={{
          height,
          width,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transform: panPosition.interpolate(
            (x, y) => `translate3d(${x}px, ${y}px, 1px)`
          ),
        }}
      >
        <AbsoluteContainer style={{ position: "fixed", height, width }} />
        {editActive ? (
          <animated.div
            style={{
              position: "absolute",
              boxSizing: "border-box",
              left: bounds.right * -1,
              right: bounds.left,
              bottom: bounds.top,
              top: bounds.bottom * -1,
              border: "1px dashed #cdcdcd",
            }}
          />
        ) : null}

        {adminFunctions ? (
          <UploadWidget
            deactivate={() => setSelectedItemId(null)}
            onUpload={handleUpload}
            {...selectedItemId}
          />
        ) : null}
        {editedItems
          ? Object.keys(editedItems)
              .sort(
                (a, b) =>
                  (editedItems[a].zIndex || 0) - (editedItems[b].zIndex || 0)
              )
              .map((itemKey) => (
                <MoodboardItem
                  editActive={editActive}
                  select={() => handleSelectItem(itemKey)}
                  handleMove={(movement, callback) =>
                    handleMoveItem(itemKey, movement, callback)
                  }
                  handleScale={(scaleAmount, callback) =>
                    handleScaleItem(itemKey, scaleAmount, callback)
                  }
                  selected={itemKey === selectedItemId}
                  key={`moodboard_item_${itemKey}`}
                  itemId={itemKey}
                  {...editedItems[itemKey]}
                />
              ))
          : null}
        {selectedMode === "Edit" ? (
          <span
            style={{
              position: "absolute",
              fontSize: "48px",
              fontWeight: "300",
              color: "#999",
              fontFamily: "Helvetica, Arial, sans-serif",
              userSelect: "none",
              pointerEvents: "none",
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            +
          </span>
        ) : null}
      </animated.div>
      {adminFunctions && editedItems ? (
        <ItemControlPanel
          item={selectedItemId ? editedItems[selectedItemId] : null}
          onDelete={deleteSelectedItem}
        />
      ) : null}
      {adminFunctions ? (
        <Selector
          options={["View", "Edit"]}
          select={setSelectedMode}
          selected={selectedMode}
        />
      ) : null}
      {adminFunctions && isMobile ? (
        <MobileAddButton
          visible={editActive && !selectedItemId}
          onAdd={handleMobileAdd}
        />
      ) : null}
    </RelativeContainer>
  );
}
